import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectUsersPage } from '../slice'
import { useAppDispatch } from '@user/store/hooks'
import { getUserList, getUserListSearch } from '../api'
import { toast } from '@sales-center/components'

export const useUserSearch = (
  query: string,
  skip?: number,
  take?: number,
  orderBy?: string,
) => {
  const dispatch = useAppDispatch()
  const users = useSelector(selectUsersPage)
  const [status, setStatus] = useState<'pending' | 'success' | 'fail'>(
    'pending',
  )
  // only the latest load may set status, so an older response can't end
  // the loading state while a newer request is still running
  const latestLoad = useRef(0)

  const trimmedQuery = useMemo(() => query?.trim() ?? '', [query])

  const loadUsers = useCallback(async () => {
    const load = ++latestLoad.current
    setStatus('pending')

    const result = trimmedQuery
      ? await dispatch(
          getUserListSearch({
            search: trimmedQuery,
            skip,
            take,
            orderBy,
            forGrid: true,
          }),
        )
      : await dispatch(
          getUserList({
            skip,
            take,
            orderBy,
            forGrid: true,
          }),
        )

    if (load !== latestLoad.current) return result

    if (
      getUserList.rejected.match(result) ||
      getUserListSearch.rejected.match(result)
    ) {
      setStatus('fail')
      toast.error('Error loading user list')
    } else {
      setStatus('success')
    }

    return result
  }, [dispatch, trimmedQuery, skip, take, orderBy])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  return { users, status, reload: loadUsers }
}
